import type {FieldId, FieldTypeId, RecordIdFieldTypeId, UUID} from 'src/typings';
import type {NonEmptyArray} from './array-utils';
import {assertHasAtLeastElements} from './array-utils';
import {assertSameLength} from './array-utils';
import {zip} from './array-utils';
import {assertIsNotEmpty} from './array-utils';
import {exists, isNull} from 'shared/utils/custom-utils';
import {assertIsNonNegative} from 'shared/utils/number-utils';
import {assertIsNonEmptyString} from 'shared/utils/string-utils';

const DELIMITER = '.'
const REPETITION_DELIMITER = '#'

export function asFieldId(rawFieldId: string): FieldId {
    assertIsNonEmptyString(rawFieldId);
    return rawFieldId as FieldId;
}

export function asFieldTypeId(rawFieldTypeId: string): FieldTypeId {
    assertIsNonEmptyString(rawFieldTypeId);
    return rawFieldTypeId as FieldTypeId;
}

/**
 *
 * @param fieldTypeId e.g. '245.main.a'
 *
 * @returns fieldTypeId split into individual segments e.g. ['245', 'main', 'a']
 */
export function splitFieldTypeId(fieldTypeId: FieldTypeId): NonEmptyArray<FieldTypeId> {
    return splitFieldIdOrFieldTypeId(fieldTypeId);
}

/**
 *
 * @param fieldId e.g. '245#0.main#1.a#2'
 *
 * @returns fieldTypeId split into individual segments e.g. ['245#0', 'main#1', 'a#3']
 */
export function splitFieldId(fieldId: FieldId): NonEmptyArray<FieldId> {
    return splitFieldIdOrFieldTypeId(fieldId);
}

function splitFieldIdOrFieldTypeId<T extends FieldId | FieldTypeId>(value: T): NonEmptyArray<T> {
    assertIsNonEmptyString(value);
    const segments = value.split(DELIMITER);
    assertIsNotEmpty(segments);
    return segments as NonEmptyArray<T>;
}

/**
 *
 * @param fieldTypeId e.g. '245.main.a'
 *
 * @returns fieldTypeId parsed into path e.g. ['245', '245.main', '245.main.a']
 */
export function parseFieldTypeIdToPath(fieldTypeId: FieldTypeId): NonEmptyArray<FieldTypeId> {
    return parseFieldIdOrFieldTypeId(fieldTypeId);
}

/**
 *
 * @param fieldId e.g. '245#0.main#1.a#2'
 *
 * @returns fieldId parsed into path e.g. ['245#0', '245#0.main#1', '245#0.main#1.a#3']
 */
export function parseFieldIdToPath(fieldId: FieldId): NonEmptyArray<FieldId> {
    return parseFieldIdOrFieldTypeId(fieldId);
}

function parseFieldIdOrFieldTypeId<T extends FieldId | FieldTypeId>(value: T): NonEmptyArray<T> {
    assertIsNonEmptyString(value);
    const codes = value.split(DELIMITER);
    const first = codes.shift();
    let res = [first];
    for (const code of codes) {
        res = [...res, `${res[res.length - 1]}${DELIMITER}${code}`];
    }
    assertIsNotEmpty(res);
    return res as NonEmptyArray<T>;
}

export function convertFieldTypeIdToFieldId(fieldTypeId: FieldTypeId, repetitionNumbers: number[]): FieldId {
    assertIsNotEmpty(repetitionNumbers)
    const fieldTypeIdSegments = splitFieldTypeId(fieldTypeId);
    assertSameLength(fieldTypeIdSegments, repetitionNumbers);
    return zip(fieldTypeIdSegments, repetitionNumbers)
        .map(([fieldTypeIdSegment, repetitionNumber]) => convertFieldTypeIdSegmentToFieldIdSegment(fieldTypeIdSegment, repetitionNumber))
        .reduce((fieldId, fieldIdSegment) => isNull(fieldId) ? fieldIdSegment : appendFieldIdSegment(fieldId, fieldIdSegment), null)
}

export function convertFieldTypeIdSegmentToFieldIdSegment(fieldTypeIdSegment: FieldTypeId, repetitionNumber: number): FieldId {
    assertIsNonEmptyString(fieldTypeIdSegment);
    assertIsNonNegative(repetitionNumber);
    return `${fieldTypeIdSegment}${REPETITION_DELIMITER}${repetitionNumber}` as FieldId;
}

export function appendFieldIdSegment(fieldId: FieldId, fieldIdSegment: FieldId): FieldId {
    assertIsNonEmptyString(fieldId);
    assertIsNonEmptyString(fieldIdSegment);
    return `${fieldId}${DELIMITER}${fieldIdSegment}` as FieldId;
}

export function convertFieldIdToFieldTypeId(fieldId: FieldId): FieldTypeId {
    assertIsNonEmptyString(fieldId);
    const fieldRepetitionRegex = /#\d+/g;
    return fieldId.replaceAll(fieldRepetitionRegex, '') as FieldTypeId;
}

export function parseRepetitionsFromFieldId(fieldId: FieldId): NonEmptyArray<number> {
    assertIsNonEmptyString(fieldId);
    const fieldRepetitionRegex = /#(\d+)/g;
    // match numbers as group and parse the group as int
    const repetitions = [...fieldId.matchAll(fieldRepetitionRegex)].map((match) => parseInt(match[1], 10));
    assertIsNotEmpty(repetitions);
    return repetitions;
}

export function isTopField(fieldId: FieldId): boolean {
    assertIsNonEmptyString(fieldId);
    const segments = splitFieldId(fieldId);
    assertIsNotEmpty(segments);
    return segments.length === 1;
}

export function getParentFieldId(fieldId: FieldId): FieldId {
    assertIsNonEmptyString(fieldId);
    const segments = splitFieldId(fieldId);
    assertHasAtLeastElements(segments, 2, () => `${fieldId} is root and has no parent fieldId`);
    return segments
        .slice(0, -1)
        .reduce((parentFieldId, fieldIdSegment) => isNull(parentFieldId) ? fieldIdSegment : appendFieldIdSegment(parentFieldId, fieldIdSegment), null)
}

export function createRecordIdFieldTypeId(recordId:  UUID, fieldTypeId: FieldTypeId): RecordIdFieldTypeId {
    return `${recordId}:${fieldTypeId}`;
}

export function mapFieldTypeIdToDynamicFieldSearchParamName(fieldTypeId: FieldTypeId, type?: 'link' | 'origin' | 'val' | 'sort'): string {
    let paramName = `field_${fieldTypeId.replaceAll('.', '_')}`;
    if (exists(type)) {
        paramName = `${paramName}_${type}`;
    }
    return paramName;
}